import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Globe, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useLanguage } from '../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, getLanguage } from '../i18n/locales';

const matches = (lang, q) => {
  if (!q) return true;
  const needle = q.toLowerCase();
  return (
    lang.code.toLowerCase().includes(needle) ||
    lang.name.toLowerCase().includes(needle) ||
    lang.native.toLowerCase().includes(needle)
  );
};

/**
 * Variants:
 * - "navbar"    - compact pill for the marketing navbar (flag + code)
 * - "settings"  - full-width tile for the settings panel
 * - "icon"      - icon-only (Globe) for tight spots
 */
const LanguageSwitcher = ({ variant = 'navbar', className = '', side = 'bottom', align = 'end' }) => {
  const { t } = useTranslation();
  const { language, setLanguage, switchLanguageInUrl } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const current = getLanguage(language);
  const isPublicPath = typeof window !== 'undefined' && !window.location.pathname.startsWith('/app');

  const filtered = useMemo(
    () => SUPPORTED_LANGUAGES.filter((l) => matches(l, query)),
    [query],
  );

  const onPick = (code) => {
    if (code === language) {
      setOpen(false);
      return;
    }
    setOpen(false);
    if (isPublicPath) {
      // Swap the URL prefix and reload - keeps language stable across deep links and SEO.
      switchLanguageInUrl(code);
    } else {
      setLanguage(code);
    }
  };

  const triggerBase =
    'inline-flex items-center gap-2 rounded-lg border border-border hover:border-input transition-colors text-sm font-medium';

  let trigger;
  if (variant === 'icon') {
    trigger = (
      <button
        className={`inline-flex items-center gap-1.5 px-2 h-8 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors ${className}`}
        aria-label={t('language_switcher.aria', 'Change language')}
      >
        <span className="text-sm leading-none">{current.flag}</span>
        <span className="uppercase tracking-wide text-[11px] font-medium">{current.code}</span>
      </button>
    );
  } else if (variant === 'settings') {
    trigger = (
      <button
        className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border border-input bg-background hover:bg-accent text-sm ${className}`}
        aria-label={t('language_switcher.aria', 'Change language')}
      >
        <span className="flex items-center gap-2 min-w-0">
          <span className="text-base leading-none">{current.flag}</span>
          <span className="truncate">{current.native}</span>
          <span className="text-xs text-muted-foreground truncate">· {current.name}</span>
        </span>
        <Globe className="w-4 h-4 text-muted-foreground" />
      </button>
    );
  } else {
    trigger = (
      <button
        className={`${triggerBase} px-3 py-2 text-foreground/80 hover:text-foreground ${className}`}
        aria-label={t('language_switcher.aria', 'Change language')}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="uppercase tracking-wide text-xs">{current.code}</span>
      </button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-72 p-0 bg-popover border border-border text-popover-foreground"
        sideOffset={8}
      >
        <div className="p-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('language_switcher.search_placeholder', 'Search languages…')}
              className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md bg-background border border-input focus:border-amber-500/50 focus:outline-none placeholder:text-muted-foreground text-foreground"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto py-1" role="listbox">
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-xs text-muted-foreground text-center">
              {t('language_switcher.no_results', 'No languages match.')}
            </div>
          )}
          {filtered.map((lang) => {
            const active = lang.code === language;
            return (
              <button
                key={lang.code}
                role="option"
                aria-selected={active}
                onClick={() => onPick(lang.code)}
                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-accent transition-colors ${
                  active ? 'bg-accent' : ''
                }`}
              >
                <span className="text-base leading-none w-6 text-center">{lang.flag}</span>
                <span className="flex-1 min-w-0">
                  <span className={`block truncate ${active ? 'text-amber-500 font-medium' : 'text-foreground'}`}>
                    {lang.native}
                  </span>
                  <span className="block text-xs text-muted-foreground truncate">{lang.name}</span>
                </span>
                {active && <Check className="w-4 h-4 text-amber-500 shrink-0" />}
              </button>
            );
          })}
        </div>
        <div className="px-3 py-2 border-t border-border text-[11px] text-muted-foreground">
          {isPublicPath
            ? t('language_switcher.hint_public', 'URL updates so you can share this language.')
            : t('language_switcher.hint_app', 'Saved to your profile.')}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default LanguageSwitcher;
